import { isAway, isFoldedThisHand, isSpectatingOut, isWaitingNextHand } from './playerLedger';
import { t } from '../i18n';

const VOLUNTARY = new Set(['CALL', 'RAISE', 'ALL_IN']);

function lastStreetAction(roundActions, name) {
  const list = Array.isArray(roundActions) ? roundActions : [];
  for (let i = list.length - 1; i >= 0; i -= 1) {
    const a = list[i];
    if (!a || a.kind === 'RETURN') continue;
    if (a.name === name && a.kind) return a;
  }
  return null;
}

// Post-flop, the first chips in are a bet. Pre-flop the blinds are already
// out, so the first voluntary increase is a raise.
function isOpeningBet(roundActions, name, stage) {
  if (stage === 'PRE_FLOP') return false;
  const list = Array.isArray(roundActions) ? roundActions : [];
  for (const a of list) {
    if (!a || a.kind === 'RETURN') continue;
    if (a.name === name && a.kind === 'RAISE') return true;
    if (VOLUNTARY.has(a.kind)) return false;
  }
  return true;
}

export function isHandAwarded(winnerInfo) {
  return Array.isArray(winnerInfo?.winnerIds) && winnerInfo.winnerIds.length > 0;
}

/** Zero street/hand bets after pots are already in stacks. Display-only — does not move chips. */
export function clearAwardedPlayerBets(player) {
  return {
    ...player,
    totalPotContribution: 0,
    currentRoundBet: 0,
  };
}

/** Game-state fields that must reset when a pot is awarded. */
export function awardedHandGameState(gameState, winnerInfo, extra = {}) {
  return {
    ...gameState,
    stage: 'SHOWDOWN',
    currentTurnPlayerId: null,
    pots: [{ id: 'main', amount: 0, eligiblePlayers: [], isMainPot: true }],
    canAdvanceStage: false,
    nextStage: null,
    nextStageName: null,
    roundActions: [],
    winnerInfo,
    ...extra,
  };
}

/** Same post-award write both fold-win (`processAction`) and showdown (`handleConfirmWinner`) use. */
export function applyAwardedHand(players, gameState, awardResult, winnerInfo, extraGameState = {}) {
  return {
    players: (awardResult.updatedPlayers || []).map(clearAwardedPlayerBets),
    gameState: awardedHandGameState(gameState, winnerInfo, extraGameState),
  };
}

// Plain reserved-line copy for this street. Empty when the street is new
// and the player has not acted (except posted blinds, which stay until they
// act). Folded seats keep "Fold" for the rest of the hand. After the pot is
// awarded, leftover Bet / Call / Raise / SB / BB amounts clear; Fold stays.
export function streetActionLabel(player, {
  roundActions = [],
  stage,
  formatCurrency,
  winnerInfo,
  handAwarded,
} = {}) {
  if (!player) return { text: '', mute: false };
  if (isAway(player)) {
    const inPot = (player.totalPotContribution || 0) > 0 || (player.currentRoundBet || 0) > 0;
    if (inPot && (player.hasFolded || player.isActive === false)) {
      return { text: t('street.fold'), mute: true };
    }
    if (!inPot) return { text: '', mute: false };
  }
  if (isSpectatingOut(player)) return { text: t('street.out'), mute: true };
  if (isWaitingNextHand(player) && !isAway(player)) return { text: '', mute: false };
  if (isFoldedThisHand(player)) return { text: t('street.fold'), mute: true };
  if (handAwarded === true || isHandAwarded(winnerInfo)) return { text: '', mute: false };
  const money = typeof formatCurrency === 'function'
    ? formatCurrency
    : (cents) => `$${((cents || 0) / 100).toFixed(2)}`;

  const last = lastStreetAction(roundActions, player.name);
  if (last) {
    if (last.kind === 'FOLD') return { text: t('street.fold'), mute: true };
    if (last.kind === 'CHECK') return { text: t('street.check'), mute: false };
    const amt = last.amount != null ? last.amount : (player.currentRoundBet || 0);
    if (player.isAllIn || last.kind === 'ALL_IN') {
      return { text: amt > 0 ? t('street.allIn', { amount: money(amt) }) : t('street.allInBare'), mute: false };
    }
    if (last.kind === 'CALL') {
      return { text: t('street.call', { amount: money(amt) }), mute: false };
    }
    if (last.kind === 'RAISE') {
      const key = isOpeningBet(roundActions, player.name, stage) ? 'street.bet' : 'street.raise';
      return { text: t(key, { amount: money(amt) }), mute: false };
    }
  }

  const bet = player.currentRoundBet || 0;
  if (!player.hasActedThisRound && bet > 0) {
    const role = player.role;
    if (role === 'SMALL_BLIND' || role === 'DEALER_SMALL_BLIND') {
      return { text: t('street.sb', { amount: money(bet) }), mute: false };
    }
    if (role === 'BIG_BLIND') {
      return { text: t('street.bb', { amount: money(bet) }), mute: false };
    }
  }

  return { text: '', mute: false };
}
